<template>
  <div class="app-container">
    <!-- 桌面端三栏布局 -->
    <div class="hidden lg:flex h-screen">
      <!-- 左侧数据面板 -->
      <div 
        class="transition-all duration-500 ease-in-out elegant-sidebar slide-elegant" 
        :class="showDataPanel ? 'w-80' : 'w-0'"
      >
        <div v-show="showDataPanel" class="w-80 h-full fade-elegant">
          <DataPanel
            :chart-data="chartData"
            :source="source"
            :target="target"
            :value="value"
            @open-price="priceVisible = true"
            @add-data="addData"
            @delete-data="deleteData"
            @change-lang="changeLang"
            @open-service="serviceVisible = true"
          />
        </div>
      </div>
      
      <!-- 左侧面板切换按钮 -->
      <div class="flex flex-col justify-center">
        <button 
          @click="toggleDataPanel"
          class="panel-toggle-elegant rounded-r-2xl"
        >
          <el-icon>
            <ArrowLeft v-if="showDataPanel" />
            <ArrowRight v-else />
          </el-icon>
        </button>
      </div>

      <!-- 中间图表区域 -->
      <div class="flex-1 flex flex-col chart-area-elegant">
        <ChartArea
          ref="chartAreaRef"
          :image-width="imageWidth"
          :image-height="imageHeight"
          :background-color="backgroundColor"
          :zoom-level="zoomLevel"
          :chart-data="chartData"
          :orient="orient"
          :node-align="nodeAlign"
          :label-position="labelPosition"
          :line-style-color="lineStyleColor"
          :selected-line-color-curveness="selectedLineColorCurveness"
          @zoom-in="zoomIn"
          @zoom-out="zoomOut"
          @reset-zoom="resetZoom"
        />
      </div>
      
      <!-- 右侧面板切换按钮 -->
      <div class="flex flex-col justify-center">
        <button 
          @click="toggleOptionsPanel"
          class="panel-toggle-elegant rounded-l-2xl"
        >
          <el-icon>
            <ArrowRight v-if="showOptionsPanel" />
            <ArrowLeft v-else />
          </el-icon>
        </button>
      </div>

      <!-- 右侧选项面板 -->
      <div 
        class="transition-all duration-500 ease-in-out elegant-sidebar border-l border-r-0 slide-elegant" 
        :class="showOptionsPanel ? 'w-80' : 'w-0'"
      >
        <div v-show="showOptionsPanel" class="w-80 h-full fade-elegant">
          <OptionsPanel
            v-model:image-width="imageWidth"
            v-model:image-height="imageHeight"
            v-model:background-color="backgroundColor"
            v-model:orient="orient"
            v-model:node-align="nodeAlign"
            v-model:label-position="labelPosition"
            v-model:line-style-color="lineStyleColor"
            v-model:selected-line-color-curveness="selectedLineColorCurveness"
            :predefine-colors="predefineColors"
            @export-layout="exportLayout"
            @update-chart="updateChart"
          />
        </div>
      </div>
    </div>

    <!-- 移动端垂直堆叠布局 -->
    <div class="lg:hidden">
      <!-- 移动端头部 -->
      <div class="toolbar-elegant px-6 py-4 flex justify-between items-center">
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
            </svg>
          </div>
          <h1 class="title-elegant">Sankey Master</h1>
        </div>
        <div class="flex space-x-2">
          <button 
            @click="showMobileDataPanel = !showMobileDataPanel"
            class="btn-primary-elegant px-4 py-2 text-sm"
          >
            {{ $t('Data') }}
          </button>
          <button 
            @click="showMobileOptionsPanel = !showMobileOptionsPanel"
            class="btn-secondary-elegant px-4 py-2 text-sm"
          >
            {{ $t('Options') }}
          </button>
        </div>
      </div>

      <!-- 移动端数据面板 -->
      <div v-if="showMobileDataPanel" class="elegant-card mx-4 my-4 fade-elegant">
        <DataPanel
          :chart-data="chartData"
          :source="source"
          :target="target"
          :value="value"
          @open-price="priceVisible = true"
          @add-data="addData"
          @delete-data="deleteData"
          @change-lang="changeLang"
          @open-service="serviceVisible = true"
        />
      </div>

      <!-- 移动端图表区域 -->
      <div class="min-h-[60vh]">
        <ChartArea
          ref="chartAreaRef"
          :image-width="Math.min(imageWidth, 600)"
          :image-height="Math.min(imageHeight, 400)"
          :background-color="backgroundColor"
          :zoom-level="zoomLevel"
          :chart-data="chartData"
          :orient="orient"
          :node-align="nodeAlign"
          :label-position="labelPosition"
          :line-style-color="lineStyleColor"
          :selected-line-color-curveness="selectedLineColorCurveness"
          @zoom-in="zoomIn"
          @zoom-out="zoomOut"
          @reset-zoom="resetZoom"
        />
      </div>

      <!-- 移动端选项面板 -->
      <div v-if="showMobileOptionsPanel" class="elegant-card mx-4 my-4 fade-elegant">
        <OptionsPanel
          v-model:image-width="imageWidth"
          v-model:image-height="imageHeight"
          v-model:background-color="backgroundColor"
          v-model:orient="orient"
          v-model:node-align="nodeAlign"
          v-model:label-position="labelPosition"
          v-model:line-style-color="lineStyleColor"
          v-model:selected-line-color-curveness="selectedLineColorCurveness"
          :predefine-colors="predefineColors"
          @export-layout="exportLayout"
          @update-chart="updateChart"
        />
      </div>
    </div>

    <!-- 对话框 -->
    <el-dialog 
      v-model="priceVisible" 
      :title="$t('')" 
      :width="isMobile ? '95%' : '600px'"
      class="elegant-dialog"
      :modal-class="'elegant-modal'"
      destroy-on-close
      align-center
    >
      <el-tabs :tab-position="isMobile ? 'top' : 'left'" :label-width="isMobile ? 'auto' : '250px'">
        <el-tab-pane :label="$t('Plan')">
          <template #label>
            <el-icon><PriceTag /></el-icon>
            {{ $t('Plan') }}
          </template>
          <PricePage />
        </el-tab-pane>
        <el-tab-pane :label="$t('Account')">
          <template #label>
            <el-icon><User /></el-icon>
            {{ $t('Account') }}
          </template>
          <Account />
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <el-dialog 
      v-model="serviceVisible" 
      :title="$t('Service')" 
      :width="isMobile ? '95%' : '600px'"
      class="elegant-dialog"
      :modal-class="'elegant-modal'"
      destroy-on-close
      align-center
    >
      <Service />
    </el-dialog>
  </div>
</template>

<script setup>
import { ElNotification } from 'element-plus'
import { ArrowLeft, ArrowRight, PriceTag, User } from '@element-plus/icons-vue'
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import PricePage from './PricePage.vue'
import Service from './Service.vue'
import Account from './Account.vue'
import DataPanel from './DataPanel.vue'
import OptionsPanel from './OptionsPanel.vue'
import ChartArea from './ChartArea.vue'

// 使用i18n
const { t, locale } = useI18n({
  useScope: 'global'
})

// 响应式状态
const showDataPanel = ref(true)
const showOptionsPanel = ref(true)
const showMobileDataPanel = ref(false)
const showMobileOptionsPanel = ref(false)

// 移动端检测
const isMobile = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 1024
  }
  return false
})

// 数据状态
const chartData = ref([])
const source = ref('')
const target = ref('')
const value = ref(0)

// 图表配置
const backgroundColor = ref('#FFFFFF')
const imageHeight = ref(512)
const imageWidth = ref(512)
const orient = ref('horizontal')
const nodeAlign = ref('right')
const labelPosition = ref('top')
const lineStyleColor = ref('source')
const selectedLineColorCurveness = ref(0.1)
const zoomLevel = ref(1)

// 界面状态
const priceVisible = ref(false)
const serviceVisible = ref(false)
const selectedLang = ref(null)

// 引用
const chartAreaRef = ref(null)

// 预定义颜色
const predefineColors = ref([
  '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585',
  'rgba(255, 69, 0, 0.68)', 'rgb(255, 120, 0)', 'hsv(51, 100, 98)',
  'hsva(120, 40, 94, 0.5)', 'hsl(181, 100%, 37%)', 'hsla(209, 100%, 56%, 0.73)', '#c7158577'
])

// 方法

const toggleDataPanel = () => {
  showDataPanel.value = !showDataPanel.value
}

const toggleOptionsPanel = () => {
  showOptionsPanel.value = !showOptionsPanel.value
}

const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value * 1.2, 3)
}

const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value / 1.2, 0.1)
}

const resetZoom = () => {
  zoomLevel.value = 1
}

const changeLang = (lang) => {
  locale.value = lang
}


const Notify = () => {
  ElNotification({
    title: t("Warning"),
    message: t("InvalidInput"),
    position: 'top-left',
    type: 'warning'
  })
}

const hasPurchased = () => {
  const purchaseStatus = localStorage.getItem("purchaseStatus")
  const expirationTime = localStorage.getItem("expirationTime")
  return purchaseStatus === "purchased" && expirationTime && new Date() < new Date(expirationTime)
}

const addData = (data) => {
  if (hasPurchased()) {
    if (data.source && data.target && data.value > 0) {
      chartData.value.push({ source: data.source, target: data.target, value: data.value })
      updateChart()
    } else {
      Notify()
    }
  } else {
    priceVisible.value = true
  }
}

const deleteData = (index) => {
  chartData.value.splice(index, 1)
  updateChart()
}

const updateChart = () => {
  nextTick(() => {
    if (chartAreaRef.value?.updateChart) {
      chartAreaRef.value.updateChart()
    }
  })
}

const exportLayout = () => {
  if (!chartAreaRef.value) {
    ElNotification({
      title: t('Error'),
      message: t('ChartNotReady'),
      type: 'error'
    })
    return
  }

  try {
    // 调用ChartArea组件的导出方法
    chartAreaRef.value.exportLayout()
  } catch (error) {
    console.error('导出失败:', error)
    ElNotification({
      title: t('Error'),
      message: t('ExportFailed'),
      type: 'error'
    })
  }
}

const generateData = () => {
  chartData.value = [
    { source: 'A', target: 'B', value: 5 },
    { source: 'B', target: 'C', value: 3 },
    { source: 'B', target: 'D', value: 2 },
    { source: 'E', target: 'A', value: 4 },
    { source: 'M', target: 'B', value: 4 },
    { source: 'X', target: 'M', value: 4 },
    { source: 'Z', target: 'A', value: 4 },
    { source: '0', target: 'E', value: 4 }
  ]
}


// 生命周期
onMounted(() => {
  selectedLang.value = locale.value
  generateData()
})

// 监听器
watch(zoomLevel, () => {
  // zoom变化时图表会自动响应
})
</script>

<style scoped>
/* 网格背景 */
.bg-grid {
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
  background-size: 20px 20px;
}

/* 响应式断点优化 */
@media (max-width: 640px) {
  .min-h-screen {
    min-height: 100vh;
    min-height: 100dvh; /* 动态视口高度 */
  }
}

/* 触控优化 */
@media (max-width: 1024px) {
  /* 增大按钮触控区域 */
  .el-button {
    min-height: 44px;
    padding: 12px 20px;
  }
  
  /* 增大输入框触控区域 */
  .el-input {
    min-height: 44px;
  }
  
  .el-input-number {
    min-height: 44px;
  }
  
  /* 优化表格在移动端的显示 */
  .el-table {
    font-size: 14px;
  }
  
  .el-table th,
  .el-table td {
    padding: 8px 4px;
  }
}

/* 平滑过渡动画 */
.transition-panel {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 优化滚动条 */
.custom-scrollbar {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.8);
}

/* 图表区域优化 */
.chart-container {
  position: relative;
  overflow: hidden;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .chart-container {
    padding: 8px;
  }
  
  /* 移动端对话框优化 */
  .el-dialog {
    margin: 5vh auto;
    max-height: 90vh;
  }
  
  .el-dialog__body {
    max-height: 70vh;
    overflow-y: auto;
  }
}
</style>
